import { Chip } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import useCreatedByFormatter from '../../../../hooks/useCreatedByFormatter';
import { FileIngestionConfig } from '../../../../services/queries/FileIngestionQueries';
import { AgFormatterService } from '../../../../util/ag-formatter-service';
import { getIsoDateColDef } from '../../../../util/ag-grid-utils';
import { TooltipWrapperComponent } from '../../../../util/TooltipWrapperComponent';
import { ActionsCellRenderer } from '../components/ActionsCellRenderer';

interface UseFieldAutomationColDefsProps {
  onEdit: (config: FileIngestionConfig) => void;
}

export function useFieldAutomationColDefs({ onEdit }: UseFieldAutomationColDefsProps) {
  const { createdByFormatter } = useCreatedByFormatter();

  const columnDefs: ColDef<FileIngestionConfig>[] = useMemo(
    () => [
      {
        field: 'fileType',
        headerName: 'File Type',
        getQuickFilterText: (params) => params.value,
      },
      {
        field: 'displayName',
        headerName: 'Display Name',
        getQuickFilterText: (params) => params.value,
      },
      {
        field: 'description',
        headerName: 'Description',
        cellRenderer: TooltipWrapperComponent,
        getQuickFilterText: (params) => params.value || '',
      },
      {
        headerName: 'Mapped Fields',
        maxWidth: 140,
        valueGetter: (params) => params.data?.config?.fieldMappings?.length || 0,
        cellRenderer: (params: { value: number }) => {
          return params.value > 0 ? params.value : '-';
        },
      },
      {
        field: 'active',
        headerName: 'Status',
        maxWidth: 120,
        cellRenderer: (params: { value: boolean }) => {
          return (
            <Chip
              label={params.value ? 'Active' : 'Inactive'}
              color={params.value ? 'success' : 'default'}
              size='small'
            />
          );
        },
      },
      getIsoDateColDef<FileIngestionConfig>({
        field: 'createdAt',
        headerName: 'Created At',
        valueFormatter: AgFormatterService.get()?.getFormatterForId('date'),
      }),
      {
        field: 'createdBy',
        headerName: 'Created By',
        resizable: false,
        valueFormatter: createdByFormatter,
      },
      {
        minWidth: 50,
        maxWidth: 110,
        pinned: 'right',
        sortable: false,
        resizable: false,
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: { onEdit },
      },
    ],
    [createdByFormatter, onEdit]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      minWidth: 120,
      resizable: true,
      sortable: true,
      filter: false,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      suppressMovable: true,
      getQuickFilterText: () => {
        return '';
      },
    }),
    []
  );

  return {
    columnDefs,
    defaultColDef,
  };
}
